import { LivePill } from "@/components/ui/LivePill";
import { useTheme } from "@/lib/theme";
import { Link, useRouterState } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { CommandPalette } from "./CommandPalette";

const TABS = [
  { label: "PGA", to: "/pga" },
  { label: "Fast Feed", to: "/fast-feed" },
  { label: "Health", to: "/health" },
  { label: "Pricing", to: "/pricing" },
  { label: "Opportunity", to: "/opportunity" },
  { label: "Simulator", to: "/simulator" },
] as const;

function SearchButton({ className, onOpen }: { className: string; onOpen: () => void }) {
  return (
    <button type="button" onClick={onOpen} aria-label="Search (⌘K)" className={className}>
      <svg aria-hidden="true" width="12" height="12" viewBox="0 0 16 16" fill="none">
        <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.5" />
        <path d="M11 11l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
      <span className="topbar-search-text">Search blocks, txs…</span>
      <kbd className="topbar-search-kbd">⌘K</kbd>
    </button>
  );
}

export function TopBar() {
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const routerState = useRouterState();
  const { theme, toggle } = useTheme();
  const currentPath = routerState.location.pathname;
  const headerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
      if (e.key === "Escape") setMenuOpen(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    function onOutside(e: MouseEvent) {
      if (headerRef.current && !headerRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, [menuOpen]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: close menu on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [currentPath]);

  return (
    <>
      <header className="topbar-header" ref={headerRef}>
        {/* Logo */}
        <Link to="/pga" className="topbar-logo">
          <span className="topbar-wordmark">priority watch</span>
          <span className="topbar-badge">PGA</span>
        </Link>

        {/* Desktop nav (>900px) */}
        <nav className="topbar-nav" aria-label="Main navigation">
          {TABS.map((tab) => {
            const active = currentPath === tab.to || currentPath.startsWith(`${tab.to}/`);
            return (
              <Link
                key={tab.to}
                to={tab.to}
                className={`topbar-tab${active ? " topbar-tab-active" : ""}`}
                aria-current={active ? "page" : undefined}
              >
                {tab.label}
              </Link>
            );
          })}
        </nav>

        {/* Stretched search bar — fills middle gap on tablet (640–900px) */}
        <SearchButton className="topbar-search-tablet" onOpen={() => setPaletteOpen(true)} />

        {/* Right-side actions */}
        <div className="topbar-right">
          {/* Full search — desktop only */}
          <SearchButton className="topbar-search" onOpen={() => setPaletteOpen(true)} />

          <LivePill />

          <button
            type="button"
            aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            onClick={toggle}
            className="topbar-icon-btn"
          >
            {theme === "dark" ? "☀" : "🌙"}
          </button>

          <button
            type="button"
            aria-label="Settings"
            className="topbar-icon-btn topbar-desktop-only"
          >
            ⚙
          </button>
          <button type="button" aria-label="Help" className="topbar-icon-btn topbar-desktop-only">
            ?
          </button>

          {/* Hamburger — tablet/mobile only */}
          <button
            type="button"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((o) => !o)}
            className="topbar-hamburger"
          >
            {menuOpen ? (
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
                <path
                  d="M3 3l12 12M15 3L3 15"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
                <path
                  d="M3 5h12M3 9h12M3 13h12"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
              </svg>
            )}
          </button>
        </div>

        {/* Dropdown (tablet/mobile) */}
        {menuOpen && (
          <nav className="topbar-mobile-menu" aria-label="Navigation menu">
            {TABS.map((tab) => {
              const active = currentPath === tab.to || currentPath.startsWith(`${tab.to}/`);
              return (
                <Link
                  key={tab.to}
                  to={tab.to}
                  className={`topbar-mobile-tab${active ? " topbar-mobile-tab-active" : ""}`}
                  aria-current={active ? "page" : undefined}
                >
                  {tab.label}
                </Link>
              );
            })}
          </nav>
        )}
      </header>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </>
  );
}
